import React from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { BookOpen, Code, Rocket, Zap, Users, Globe, Star, ArrowRight, ExternalLink, Copy, Check } from 'lucide-react';

const DocsPage = () => {
  const { connected } = useWallet();

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-white">TokenOS</span>
            <span className="bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent"> Documentation</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            AI-powered operating system that helps you build and launch tokenized businesses, DeFi platforms, and crypto projects using natural language prompts
          </p>
        </div>

        {/* Getting Started */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Rocket className="h-8 w-8 text-purple-400 mr-3" />
            Getting Started
          </h2>
          
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <div className="space-y-6">
              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-white mb-2">1. Natural Language Development</h3>
                <p className="text-gray-300">
                  Simply describe your project idea in plain English and our AI generates complete, production-ready code
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-white mb-2">2. Instant Deployment</h3>
                <p className="text-gray-300">
                  Deploy your project instantly with live hosting and get CNAME records for custom domains
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-white mb-2">3. Token Launch</h3>
                <p className="text-gray-300">
                  Launch custom tokens and memecoins on Solana with integrated wallet generation
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-white mb-2">4. AI Guidance</h3>
                <p className="text-gray-300">
                  Ask questions and get guidance from our AI assistant throughout your development journey
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features & Capabilities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Code className="h-6 w-6 text-purple-400 mr-3" />
              AI-Powered Development
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">Natural language to code generation</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">React/Next.js applications</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">Smart contract development</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">DeFi protocol creation</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">NFT marketplace building</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">General AI assistance and guidance</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Globe className="h-6 w-6 text-purple-400 mr-3" />
              Blockchain Integration
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">SPL Token Creation on Solana</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">Solana Wallet Generation</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">Smart Contract Deployment</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">DeFi Protocol Building</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">Memecoin Launching</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300">DAO Governance Systems</span>
              </div>
            </div>
          </div>
        </div>

        {/* Deployment & Hosting */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Zap className="h-8 w-8 text-purple-400 mr-3" />
            Deployment & Hosting
          </h2>
          
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <p className="text-gray-300 mb-6">TokenOS provides instant deployment and hosting for your generated projects:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Instant deployment with live URLs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">CNAME records for custom domains</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Automatic SSL certificates</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Global CDN distribution</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">High-performance hosting infrastructure</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Distribution */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Revenue Distribution</h2>
          
          <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-8">
            <h3 className="text-xl font-bold text-white mb-4">Monthly Revenue Sharing</h3>
            <p className="text-gray-300 mb-6">
              TokenOS believes in sharing success with our community. <strong>50% of all net platform revenue is distributed to $TOS token holders every month.</strong> This includes revenue from credit purchases, premium features, and enterprise subscriptions.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Revenue distribution occurs automatically on the 1st of each month</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Distribution is proportional to your $TOS token holdings</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">No minimum holding requirement - all holders participate</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Payments are made directly to your connected wallet</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Revenue reports are published monthly for transparency</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg">
              <p className="text-purple-300 font-medium">
                💡 Pro Tip: Hold more $TOS tokens to increase your share of monthly revenue distributions!
              </p>
            </div>
          </div>
        </div>

        {/* Example Prompts */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Star className="h-8 w-8 text-purple-400 mr-3" />
            Example Prompts
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <p className="text-gray-300 mb-4 italic">
                "Create a DeFi yield farming platform with staking rewards"
              </p>
              <div className="text-xs text-purple-400">
                Features: Yield farming, Staking, Rewards distribution
              </div>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <p className="text-gray-300 mb-4 italic">
                "Build a NFT marketplace with auction features"
              </p>
              <div className="text-xs text-purple-400">
                Features: NFT minting, Marketplace, Auction system
              </div>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <p className="text-gray-300 mb-4 italic">
                "Create a meme coin dashboard with holder analytics"
              </p>
              <div className="text-xs text-purple-400">
                Features: Token analytics, Holder tracking, Dashboard UI
              </div>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <p className="text-gray-300 mb-4 italic">
                "What are the best practices for tokenomics?"
              </p>
              <div className="text-xs text-purple-400">
                Type: AI guidance and consultation
              </div>
            </div>
          </div>
        </div>

        {/* Community */}
        <div className="mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <Users className="h-5 w-5 text-blue-400 mr-2" />
              Community & Support
            </h3>
            <div className="space-y-3">
              <a
                href="https://t.me/tokenos_ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-300 hover:text-white transition-colors"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Join our Telegram Community
              </a>
              <a
                href="https://x.com/TokenOS_AI"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-300 hover:text-white transition-colors"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Follow us on X (Twitter)
              </a>
              <a
                href="https://www.youtube.com/channel/UC66OxY0dSR5yiLxNQScSbMg"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-300 hover:text-white transition-colors"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Subscribe to our YouTube Channel
              </a>
              <a
                href="mailto:info@tokenos.ai"
                className="flex items-center text-gray-300 hover:text-white transition-colors"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Contact Support via Email
              </a>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Start Building?</h2>
          <p className="text-gray-300 mb-6">
            Join thousands of developers using TokenOS to build the future of Web3
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Start Building Now
            </a>
            <a
              href="https://t.me/tokenos_ai"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Join Community
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;